import { productAlias } from './ProductAlias'
import { productFingerprint } from './ProductFingerprint'
import { productNormalizer } from './ProductNormalizer'
import type { ParsedProductHints, ProductMatchGroup, ProductModel } from './ProductTypes'

/**
 * ProductMatcher — groups equivalent marketplace descriptions into one product.
 */
export class ProductMatcher {
  sameProduct(a: ParsedProductHints, b: ParsedProductHints): boolean {
    const fpA = productFingerprint.build(a)
    const fpB = productFingerprint.build(b)
    if (productFingerprint.equals(fpA, fpB)) return true

    if (a.partNumber && b.partNumber && equals(a.partNumber, b.partNumber)) return true

    if (a.brand && b.brand && equals(a.brand, b.brand) && a.model && b.model && equals(a.model, b.model)) {
      if (!a.capacity || !b.capacity || equals(a.capacity, b.capacity)) return true
    }

    const aliasHit =
      productAlias.resolve(a.raw) &&
      productAlias.resolve(a.raw) === productAlias.resolve(b.raw)
    return Boolean(aliasHit)
  }

  group(descriptions: string[], resolve: (raw: string) => ProductModel): ProductMatchGroup[] {
    const map = new Map<string, ProductMatchGroup>()

    for (const description of descriptions) {
      const product = resolve(description)
      const key = this.groupKey(product)
      const existing = map.get(key)
      if (existing) {
        existing.sources.push(description)
        existing.confidence = Math.min(1, existing.confidence + 0.05)
        // Prefer richer fingerprint (with part number) as canonical
        if (
          product.partNumber &&
          (!existing.product.partNumber || product.partNumber.length > existing.product.partNumber.length)
        ) {
          existing.product = product
          existing.fingerprint = product.fingerprint
        }
        continue
      }
      map.set(key, {
        fingerprint: product.fingerprint,
        product,
        sources: [description],
        confidence: 0.85,
      })
    }

    return [...map.values()].sort((a, b) => b.sources.length - a.sources.length)
  }

  private groupKey(product: ProductModel): string {
    if (product.brand && product.model) {
      return `bm:${productNormalizer.normalize(product.brand)}:${productNormalizer.normalize(product.model)}`
    }
    if (product.partNumber) {
      return `pn:${productNormalizer.normalize(product.partNumber)}`
    }
    return `fp:${product.fingerprint}`
  }

  /** Similarity score 0–1 for ranking near-matches */
  score(a: ParsedProductHints, b: ParsedProductHints): number {
    let score = 0
    if (a.partNumber && b.partNumber && equals(a.partNumber, b.partNumber)) score += 0.5
    if (a.brand && b.brand && equals(a.brand, b.brand)) score += 0.2
    if (a.model && b.model && equals(a.model, b.model)) score += 0.2
    if (a.category && b.category && equals(a.category, b.category)) score += 0.05
    if (a.capacity && b.capacity && equals(a.capacity, b.capacity)) score += 0.05
    return Math.min(1, score)
  }
}

function equals(a: string, b: string): boolean {
  return productNormalizer.normalize(a) === productNormalizer.normalize(b)
}

export const productMatcher = new ProductMatcher()
